/**
 * =============================================================================
 * ActivityTimeline — B-6 shared primitive (ADR-025 §6.2; design-v3 §F)
 * =============================================================================
 *
 * The presentational skeleton EXTRACTED from B-5's `QuestionChangesTimeline`
 * (the named incumbent): a semantic <ol> of entries, each rendering
 * title + relative <time> + byline + display-safe detail rows + an optional
 * "reason recorded" chip. DOM is byte-identical to the incumbent's inline JSX.
 *
 * Extraction direction is THIN-DELEGATE (adjudication CL-8/A-3):
 * `QuestionChangesTimeline` keeps its file, export, data hook, ADR-023
 * display-safe handling, subject-scope guard, copy, and testids — it passes
 * pre-formatted strings here. This component owns NO data semantics: no
 * fetching, no filtering, no date math, no copy. `AuditPanel`'s Table rendering
 * of the same rows is deliberately NOT a consumer (different surface;
 * different `humanise` behavior — design-v3 §F).
 * =============================================================================
 */

import React from 'react';
import { Box, Flex, FlexItem, Text } from '@bigcommerce/big-design';
import { StatusChip } from './StatusChip';

export interface ActivityTimelineEntry {
  /** Stable id — becomes the li key and `${testId}-entry-${id}` testid. */
  id: string;
  /** Entry heading (caller-humanised). */
  title: string;
  /** Machine-readable timestamp for the <time> element. */
  dateTime: string;
  /** Hover title for the <time> element (caller-formatted). */
  timestampTitle: string;
  /** Visible relative time (caller-formatted). */
  timestampDisplay: string;
  /** Attribution line (caller-owned copy, e.g. "By moderator"). */
  byline: string;
  /** Display-safe detail rows. */
  details: ReadonlyArray<{ label: string; value: string }>;
  /** When set, renders a secondary chip with this label (e.g. "Reason recorded"). */
  chipLabel?: string;
}

export interface ActivityTimelineProps {
  entries: ReadonlyArray<ActivityTimelineEntry>;
  /** Accessible name of the list. */
  ariaLabel: string;
  /** Testid prefix: the ol gets `${testId}-results`, entries `${testId}-entry-${id}`. */
  testId: string;
  /** Rendered when an entry has no details and no chip (caller-owned copy). */
  emptyDetailsFallback: string;
}

function TimelineEntry({
  entry,
  testId,
  emptyDetailsFallback,
}: {
  entry: ActivityTimelineEntry;
  testId: string;
  emptyDetailsFallback: string;
}): React.ReactElement {
  return (
    <li data-testid={`${testId}-entry-${entry.id}`}>
      <Box marginBottom="medium">
        <Flex
          justifyContent="space-between"
          alignItems="flex-start"
          flexWrap="wrap"
          flexGap="0.5rem"
        >
          <FlexItem>
            <Text bold marginBottom="xxSmall">
              {entry.title}
            </Text>
          </FlexItem>
          <FlexItem>
            <Text color="secondary" marginBottom="xxSmall">
              <time dateTime={entry.dateTime} title={entry.timestampTitle}>
                {entry.timestampDisplay}
              </time>
            </Text>
          </FlexItem>
        </Flex>

        <Text color="secondary" marginBottom="xxSmall">
          {entry.byline}
        </Text>

        {entry.details.map((detail, index) => (
          <Text key={`${detail.label}-${index}`} marginBottom="xxSmall">
            <strong>{detail.label}:</strong> {detail.value}
          </Text>
        ))}
        {entry.chipLabel != null && <StatusChip label={entry.chipLabel} variant="secondary" />}
        {entry.details.length === 0 && entry.chipLabel == null && (
          <Text color="secondary" marginBottom="none">
            {emptyDetailsFallback}
          </Text>
        )}
      </Box>
    </li>
  );
}

export function ActivityTimeline({
  entries,
  ariaLabel,
  testId,
  emptyDetailsFallback,
}: ActivityTimelineProps): React.ReactElement {
  return (
    <ol aria-label={ariaLabel} data-testid={`${testId}-results`}>
      {entries.map((entry) => (
        <TimelineEntry
          key={entry.id}
          entry={entry}
          testId={testId}
          emptyDetailsFallback={emptyDetailsFallback}
        />
      ))}
    </ol>
  );
}
